import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';

import { IconsEnum } from '../../../../shared-modules/shared-icons/models/icons.enum';
import { IconColourEnum } from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import { DEFAULT_LOADER_INFINITE, ISharedLoadingG } from '../../../../shared/shared-module-ui/models/animations';
import { ISelectOption } from '../../../../shared/shared-models/custom-select/custom-select';
import { IModalDismissResult } from '../../../../shared/shared-services/overlay/modal-controller.service';
import { ISharedModalBasic } from '../../../../shared/shared-models/modals/shared-modal-basic';
import { SharedModalUtils } from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import { _DEFAULT_APPLICATION_TOOLTIPS } from '../../../core/models';
import { selectSelectedStore } from '../../../user-access/user-stores-store/user-store.selectors';
import {
  DEFAULT_DOCUMENT_ACCENT_COLOUR,
  DOCUMENT_ACCENT_COLOURS,
} from '../../models/documents/document-accent-colours';
import {
  LOCAL_PURCHASE_ORDER_LAYOUT_KEY,
  LOCAL_PURCHASE_ORDER_LAYOUT_NAME,
} from '../../models/documents/document-layout.interface';
import { IHasUnsavedChanges, UnsavedChangesService } from '../../services/unsaved-changes/unsaved-changes.service';
import { documentLayoutActions } from '../../store/document-layout.actions';
import { selectDocumentLayoutState } from '../../store/document-layout.selectors';

@Component({
  selector: 'app-feature-auto-order-document-layouts',
  standalone: false,
  templateUrl: './feature-auto-order-document-layouts.component.html',
  styleUrl: './feature-auto-order-document-layouts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureAutoOrderDocumentLayoutsComponent implements OnDestroy, IHasUnsavedChanges {
  private readonly store = inject(Store);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly modalUtils = inject(SharedModalUtils);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  private readonly state = this.store.selectSignal(selectDocumentLayoutState);
  protected readonly selectedStore = this.store.selectSignal(selectSelectedStore);
  protected readonly selectedAccentColour = signal(DEFAULT_DOCUMENT_ACCENT_COLOUR);
  protected readonly selectedLayoutKey = signal(LOCAL_PURCHASE_ORDER_LAYOUT_KEY);

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected readonly layoutSettingsTooltip =
    'Choose the purchase order PDF layout and accent colour for this store. Layout 0 is generated on the client server.';
  protected readonly loadingAnimation: ISharedLoadingG = DEFAULT_LOADER_INFINITE;
  private lastPreviewUrl: string | null = null;

  protected readonly isLocalLayout = computed(
    () => this.selectedLayoutKey() === LOCAL_PURCHASE_ORDER_LAYOUT_KEY,
  );
  protected readonly isPageLoading = computed(
    () => this.state().loadingLayouts || this.state().loadingSettings,
  );
  protected readonly isSaving = computed(() => this.state().loading);
  protected readonly isPreviewLoading = computed(() => this.state().loadingPreview);
  protected readonly loading = computed(() => this.isPageLoading() || this.isSaving());
  protected readonly error = computed(() => this.state().error);
  protected readonly previewUrl = computed(() => this.state().previewUrl);
  protected readonly previewSrc = computed((): SafeResourceUrl | null => {
    const url = this.previewUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  private readonly savedAccentColour = computed((): string => {
    const storeId = this.selectedStore()?.storeId;
    if (!storeId) {
      return DEFAULT_DOCUMENT_ACCENT_COLOUR;
    }
    const saved = this.state().settingsByStore[storeId]?.purchaseOrder?.options?.accentColour;
    const known = DOCUMENT_ACCENT_COLOURS.some((colour) => colour.key === saved);
    return known && saved ? saved : DEFAULT_DOCUMENT_ACCENT_COLOUR;
  });

  private readonly savedLayoutKey = computed((): string => {
    const storeId = this.selectedStore()?.storeId;
    if (!storeId) {
      return LOCAL_PURCHASE_ORDER_LAYOUT_KEY;
    }
    return this.state().settingsByStore[storeId]?.purchaseOrder?.layoutKey ?? LOCAL_PURCHASE_ORDER_LAYOUT_KEY;
  });

  protected readonly hasEditsBeenEdited = computed((): boolean => {
    if (this.isPageLoading()) {
      return false;
    }
    const storeId = this.selectedStore()?.storeId;
    if (!storeId) {
      return false;
    }
    if (this.selectedLayoutKey() !== this.savedLayoutKey()) {
      return true;
    }
    return this.selectedAccentColour() !== this.savedAccentColour();
  });

  protected readonly isSaveDisabled = computed(
    (): boolean => this.isSaving() || this.isPageLoading() || !this.hasEditsBeenEdited(),
  );

  protected readonly layoutOptions = computed((): Record<string, ISelectOption> => {
    const current = this.selectedLayoutKey();
    const layouts = this.state().layouts;
    const options: Record<string, ISelectOption> = {};
    const apiLayouts = layouts.filter((layout) => layout.key !== LOCAL_PURCHASE_ORDER_LAYOUT_KEY);
    const fallback = [
      { key: 'layout_1', name: 'Layout 1' },
      { key: 'layout_2', name: 'Layout 2' },
    ];
    const source = [
      { key: LOCAL_PURCHASE_ORDER_LAYOUT_KEY, name: LOCAL_PURCHASE_ORDER_LAYOUT_NAME },
      ...(apiLayouts.length ? apiLayouts : fallback),
    ];
    for (const layout of source) {
      options[layout.key] = {
        name: layout.name,
        disabled: false,
        selected: layout.key === current,
      };
    }
    return options;
  });

  protected readonly colourOptions = computed((): Record<string, ISelectOption> => {
    const current = this.selectedAccentColour();
    const options: Record<string, ISelectOption> = {};
    for (const colour of DOCUMENT_ACCENT_COLOURS) {
      options[colour.key] = {
        name: colour.name,
        disabled: false,
        selected: colour.key === current,
      };
    }
    return options;
  });

  constructor() {
    this.unsavedChangesService.registerComponent(this);

    effect(() => {
      const storeId = this.selectedStore()?.storeId;
      if (!storeId) {
        return;
      }
      this.store.dispatch(documentLayoutActions.loadLayouts());
      this.store.dispatch(documentLayoutActions.loadSettings({ storeId }));
    });
    effect(() => {
      if (this.hasEditsBeenEdited()) {
        return;
      }
      this.selectedLayoutKey.set(this.savedLayoutKey());
      this.selectedAccentColour.set(this.savedAccentColour());
    });
    effect(() => {
      const url = this.previewUrl();
      if (this.lastPreviewUrl && this.lastPreviewUrl !== url) {
        URL.revokeObjectURL(this.lastPreviewUrl);
      }
      this.lastPreviewUrl = url;
    });
  }

  get haveAnyEditsBeenMade(): boolean {
    return this.hasEditsBeenEdited();
  }

  ngOnDestroy(): void {
    if (this.lastPreviewUrl) {
      URL.revokeObjectURL(this.lastPreviewUrl);
    }
    this.store.dispatch(documentLayoutActions.clearPreview());
    this.unsavedChangesService.unregisterComponent();
  }

  protected onAccentChange(selected: Record<string, ISelectOption>): void {
    const key = Object.keys(selected).find((item) => selected[item].selected);
    if (key) {
      this.selectedAccentColour.set(key);
    }
  }

  protected onLayoutChange(selected: Record<string, ISelectOption>): void {
    const key = Object.keys(selected).find((item) => selected[item].selected);
    if (key) {
      this.selectedLayoutKey.set(key);
      if (key === LOCAL_PURCHASE_ORDER_LAYOUT_KEY) {
        this.store.dispatch(documentLayoutActions.clearPreview());
      }
    }
  }

  protected onRevertForm(form: 'layout' | 'all'): void {
    void form;
    this.revertToSaved();
  }

  protected onSubmitForm(form: 'layout' | 'all'): void {
    void form;
    this.save();
  }

  protected save(): void {
    const storeId = this.selectedStore()?.storeId;
    if (!storeId || this.isSaveDisabled()) {
      return;
    }
    this.store.dispatch(
      documentLayoutActions.saveSettings({
        storeId,
        settings: {
          purchaseOrder: {
            layoutKey: this.selectedLayoutKey(),
            options: { accentColour: this.selectedAccentColour() },
          },
        },
      }),
    );
  }

  protected preview(): void {
    const storeId = this.selectedStore()?.storeId;
    if (!storeId || this.isPreviewLoading()) {
      return;
    }
    this.store.dispatch(
      documentLayoutActions.preview({
        accentColour: this.selectedAccentColour(),
        layoutKey: this.selectedLayoutKey(),
        storeId,
      }),
    );
  }

  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modal: ISharedModalBasic = {
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      modalTitle: 'Unsaved Changes',
      contextDescription: ['You have unsaved purchase order layout changes.'],
      buttonAcceptText: 'Discard Changes & Continue',
      buttonCloseText: 'Cancel',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
    };
    const result: IModalDismissResult = await this.modalUtils.createModal(modal);
    if (result.data?.buttonPressed === 'button-accept') {
      this.revertToSaved();
      return true;
    }
    return false;
  }

  private revertToSaved(): void {
    this.selectedLayoutKey.set(this.savedLayoutKey());
    this.selectedAccentColour.set(this.savedAccentColour());
    this.store.dispatch(documentLayoutActions.clearPreview());
  }
}
